import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  ppiCreateVariantRoute,
  ppiCreateFromExistingRoute
} from '../../routes';
import LanguageSelect from '../LanguageSelect';
import Button from '../Form/Toolbar/Button';
import PublishedInstructionLink from './PublishedInstructionLink';
import { States } from '../../features/PpiContainer/constants';
import { Mode } from './constants';
import { modePropType } from '../PpsiForm/constants';
import MoreMenu from '../Form/Toolbar/MoreMenu';
import MoreMenuItem from '../Form/Toolbar/MoreMenuItem';
import PpiIfoodDiscontinueText from './PpiiFoodDiscontinueText';
import { getIsDataDirty, getCallback } from '../../features/app/selectors';
import { notify } from 'features/notificationSystem/actions';
import { openGlobalPpiDialog } from 'features/PpiContainer/actions';
import { getGlobalVariantDialog } from 'features/PpiContainer/selectors';
import { toJS } from 'utils/immutableUtils';

const PpiToolbarContent = ({
  data,
  relationshipId,
  mode,
  onSave = () => {},
  onPublish = () => {},
  oniFoodUpdatePpi = () => {},
  onGlobalUpdatePpi = () => {},
  onUnpublish = () => {},
  onCreateDraft = () => {},
  setOpenConfirm = () => {},
  completePpiData,
  globalPpis
}) => {
  const isDataDirty = useSelector(getIsDataDirty);
  const callback = useSelector(getCallback);
  const dialogState = useSelector(getGlobalVariantDialog);
  const history = useHistory();
  const dispatch = useDispatch();
  function inStatus(...status) {
    return status.includes(data.enrichmentStatus);
  }

  const updateCategory =
    data.updateNotificationList !== undefined
      ? data.updateNotificationList.length > 0
        ? data.updateNotificationList.map(i => i.category)
        : []
      : [];

  const displayCategory =
    updateCategory.length > 0 ? [...new Set(updateCategory)] : [];

  // from line no- 50 to 69 codes added for JIRA card no IFPPSI - 510
  const iFoodDiscontinuedItems =
    data.updateNotificationList !== undefined &&
    data.updateNotificationList.length > 0 &&
    data.updateNotificationList.filter(i => i.category === 'IFOOD_UPDATE');

  const iFoodDiscontinued =
    iFoodDiscontinuedItems.length > 0 &&
    iFoodDiscontinuedItems.map(i => i.message);

  const disableIfoodImportButton =
    iFoodDiscontinued.length > 0 &&
    iFoodDiscontinued.includes(
      'This Ingredient is no longer available. This Ingredient is Discontinued'
    );

  const displayiFoodImportButton = displayCategory.includes('IFOOD_UPDATE');
  const isItemDiscontinued = displayCategory.includes(
    'GLOBAL_PPI_DISCONTINUED'
  );

  const showGlobalVariant =
    data &&
    data.parentPpiId !== null &&
    data.parentPpiEnrichmentDataId !== null;

  const displayGlobalButton =
    displayCategory.length > 0 && !displayiFoodImportButton;
  const deactivateGlobalButton = displayGlobalButton && isItemDiscontinued;

  const isIfoodUpdateBtnVisible = () => {
    if (
      displayiFoodImportButton &&
      data.upToDate === false &&
      inStatus(States.DRAFT) === true
    ) {
      return true;
    }
    return false;
  };

  const isGlobalUpdateBtnVisible = () => {
    if (
      displayGlobalButton &&
      data.upToDate === false &&
      inStatus(States.DRAFT) === true
    ) {
      return true;
    }
    return false;
  };

  function createVariant() {
    if (data.upToDate === false && displayiFoodImportButton) {
      dispatch(
        notify({
          message:
            'Failed to create variant of Ingredient instruction. Reason: PPI is not up to date with iFood!',
          variant: 'error'
        })
      );
    } else {
      history.push(
        ppiCreateVariantRoute(data.ppiId, data.itemCode, data.enrichmentStatus)
      );
    }
  }

  function createGlobalVariant() {
    const globalPpi = globalPpis;
    const globalItemCode =
      globalPpi && globalPpi.find(item => item.itemCode === relationshipId);
    let getUsedPpi = [];
    // globalPpi && globalPpi.filter(i => i.id === data.parentPpiId);
    if (globalItemCode) {
      getUsedPpi =
        globalPpi &&
        globalPpi
          .filter(item => item.itemCode === globalItemCode.itemCode)
          .filter(itm => itm.status === 'PUBLISHED');
    }
    if (getUsedPpi.length === 0) {
      dispatch(
        notify({
          message: 'Global PPI not available',
          variant: 'error'
        })
      );
    } else if (data.upToDate === false && displayiFoodImportButton) {
      dispatch(
        notify({
          message:
            'Failed to create variant of Ingredient instruction. Reason: PPI is not up to date with iFood!',
          variant: 'error'
        })
      );
    } else if (getUsedPpi[0].status == 'PUBLISHED') {
      // history.push(
      //   ppiCreateFromExistingRoute(
      //     data.parentPpiId,
      //     data.ppiId,
      //     data.itemCode,
      //     data.enrichmentStatus
      //   )
      // );
      dispatch(openGlobalPpiDialog({ open: true, id: data.ppiId }));
    }
  }

  const displayPublishedLabel = () => {
    // importUpdates changes
    if (inStatus(States.PUBLISHED, States.UNPUBLISHED)) {
      const hasInProgressVersion = !!completePpiData.inProgressPPIEnrichmentData;
      if (hasInProgressVersion) {
        return 'form.goToDraft';
      }
    }
    return 'form.createDraftPpiLabel';
  };

  if (
    isDataDirty &&
    callback &&
    (mode === Mode.CREATE || inStatus(States.DRAFT))
  ) {
    onSave(data, callback);
    return null;
  }

  return (
    <>
      {disableIfoodImportButton && <PpiIfoodDiscontinueText />}
      <PublishedInstructionLink data={data} visible={inStatus(States.DRAFT)} />
      <LanguageSelect />
      <Button
        label="form.updateButtonLabel"
        onClick={oniFoodUpdatePpi}
        visible={isIfoodUpdateBtnVisible()}
        btnDisabled={disableIfoodImportButton}
      />
      <Button
        label="form.ppiGlobalButtonLabel"
        onClick={onGlobalUpdatePpi}
        visible={isGlobalUpdateBtnVisible()}
        btnDisabled={deactivateGlobalButton}
        deactivateGlobalButton={deactivateGlobalButton}
      />
      <Button
        label="form.saveButtonLabel"
        onClick={onSave}
        visible={inStatus(States.DRAFT) || mode === Mode.CREATE}
        btnDisabled={displayiFoodImportButton}
      />
      <Button
        label="form.publishButtonLabel"
        onClick={onPublish}
        visible={inStatus(States.DRAFT)}
        btnDisabled={displayiFoodImportButton}
        requireSavedData
      />
      <Button
        label={displayPublishedLabel()}
        onClick={onCreateDraft}
        visible={inStatus(States.PUBLISHED, States.UNPUBLISHED)}
        btnDisabled={disableIfoodImportButton}
      />
      <MoreMenu
        visible={
          inStatus(States.DRAFT, States.PUBLISHED) ||
          (inStatus(States.UNPUBLISHED) && !disableIfoodImportButton)
        }
      >
        <MoreMenuItem
          key="unpublish"
          onClick={onUnpublish}
          visible={inStatus(States.PUBLISHED)}
        >
          <FormattedMessage id="ppi.unpublishMenuItemLabel" />
        </MoreMenuItem>
        <MoreMenuItem
          key="createVariant"
          onClick={createVariant}
          visible={
            inStatus(States.DRAFT, States.PUBLISHED, States.UNPUBLISHED) &&
            !disableIfoodImportButton
          }
        >
          <FormattedMessage id="ppi.createVariantMenuItemLabel" />
        </MoreMenuItem>
        <MoreMenuItem
          key="createGlobalVariant"
          onClick={createGlobalVariant}
          visible={
            inStatus(States.DRAFT, States.PUBLISHED, States.UNPUBLISHED) &&
            showGlobalVariant &&
            !disableIfoodImportButton
          }
        >
          <FormattedMessage id="ppi.createVariantFromGlobalLabel" />
        </MoreMenuItem>
        <MoreMenuItem
          key="discardDraft"
          onClick={() => setOpenConfirm(true)}
          visible={inStatus(States.DRAFT)}
        >
          <FormattedMessage id="ppi.discardDraftLabel" />
        </MoreMenuItem>
      </MoreMenu>
    </>
  );
};

PpiToolbarContent.propTypes = {
  data: PropTypes.object,
  mode: modePropType.isRequired,
  onSave: PropTypes.func,
  onPublish: PropTypes.func,
  onUnpublish: PropTypes.func,
  onCreateDraft: PropTypes.func
};

export default PpiToolbarContent;
